/**
 * Permanent (301) redirects for URLs that no longer exist (TICKET-10387).
 *
 * Single source of truth for:
 *   - nuxt.config.ts  -> nitro.routeRules (dev / preview / node server only)
 *   - render.yaml     -> `routes:` redirects
 *
 * PRODUCTION: unified.to is a static site managed in the Render dashboard (service
 * "Unified-www4"), which does not read render.yaml, and routeRules redirects don't exist
 * in a static `nuxt generate` build. A redirect only goes live once it is added under
 * Render dashboard -> Unified-www4 -> Redirects/Rewrites. After editing this list, add the new
 * rules with `RENDER_API_KEY=... npm run syncredirects -- --apply` (server/utils/sync-render-redirects.ts).
 *
 * Each entry must land on a URL that returns 200 in a single hop. Never point a
 * retired page at an unrelated page (e.g. the homepage): Google treats that as a
 * soft 404. Keep render.yaml in sync by hand when editing this list.
 */
export interface ISiteRedirect {
    from: string;
    to: string;
}

const MERGE_VS_UNIFIED = '/blog/merge_vs_unified_which_unified_api_should_you_choose_in_2026';
const FUNDING_POST = '/blog/unified_secures_dollar1_1m_pre_seed_to_power_integrations_for_ai_first_companies';

export const SITE_REDIRECTS: readonly ISiteRedirect[] = [
    // Blog posts that moved or were renamed.
    {
        from: '/blog/how_virtual_webhook_retry_and_recovery_works',
        to: 'https://docs.unified.to/guides/how_virtual_webhook_retry_and_recovery_works',
    },
    {
        from: '/blog/workable_creating_candidates_and_attaching_them_to_jobs',
        to: 'https://docs.unified.to/guides/workable_creating_candidates_and_attaching_them_to_jobs',
    },
    {
        from: '/blog/replacing_polling_with_unified_webhooks_and_virtual_streams',
        to: '/blog/replacing_polling_with_event_driven_integrations_using_virtual_webhooks',
    },
    { from: '/blog/merge_vs_unified_which_unified_api_is_right_for_your_product', to: MERGE_VS_UNIFIED },
    { from: '/blog/merge_vs_unified_a_2026_comparison_for_saas_and_ai_teams', to: MERGE_VS_UNIFIED },
    // The `$` in the old funding-post slug broke the blog index link; the post is now
    // published at the `dollar` slug (see getName / safeFilename in server/utils/helpcenter.ts).
    {
        from: '/blog/unified_secures_$1_1m_pre_seed_to_power_integrations_for_ai_first_companies',
        to: FUNDING_POST,
    },
    {
        from: '/blog/unified_secures_%241_1m_pre_seed_to_power_integrations_for_ai_first_companies',
        to: FUNDING_POST,
    },

    // Retired marketing pages.
    { from: '/vs/merge', to: MERGE_VS_UNIFIED },
    { from: '/saml', to: '/login' },
    { from: '/passthrough', to: 'https://docs.unified.to/passthrough/overview' },
    { from: '/metadata', to: 'https://docs.unified.to/metadata/overview' },

    // Renamed integration / concept pages.
    { from: '/integrations/zenefits', to: '/integrations/trinet' },
    { from: '/integration-authorization', to: 'https://docs.unified.to/concepts/embedded-components' },

    // Old in-site docs paths (the docs now live on docs.unified.to).
    // pages/apidocs.vue only redirects in the browser after the page loads; crawlers get a 404.
    { from: '/apidocs', to: 'https://docs.unified.to/' },
    // docs.unified.to/intro returns a 404 status; the docs home is the introduction
    { from: '/docs/intro', to: 'https://docs.unified.to/' },
    { from: '/docs/reference/pagination', to: 'https://docs.unified.to/reference/pagination' },
    { from: '/docs/reference/rate_limits', to: 'https://docs.unified.to/reference/rate_limits' },
];

/** Pages that are removed outright (real 404, no redirect): /startup and /promo. */
export const REMOVED_PAGES: readonly string[] = ['/startup', '/promo'];

/** Pages that stay live but must not be indexed or listed in the sitemap. */
export const NOINDEX_PAGES: readonly string[] = ['/thanks'];

/**
 * Integrations that must not appear anywhere on the website (listing, detail page, sitemap, llms.txt,
 * blog links). `greenhouseingest` is internal; `gusto` is not offered publicly (TICKET-10387).
 */
export const HIDDEN_INTEGRATION_TYPES: readonly string[] = ['greenhouseingest', 'gusto'];

/** Competitor comparison pages that are retired (redirected above, excluded from prerender + sitemap). */
export const RETIRED_COMPETITOR_SLUGS: readonly string[] = ['merge'];

/** Category pages that are retired (redirected above, excluded from prerender + sitemap). */
export const RETIRED_CATEGORY_SLUGS: readonly string[] = ['saml'];

/** Routes that must not appear in sitemap.xml. */
export const SITEMAP_EXCLUDE: readonly string[] = [
    ...SITE_REDIRECTS.map((r) => r.from).filter((p) => !p.startsWith('/blog/') && !p.startsWith('/docs/')),
    ...REMOVED_PAGES,
    ...NOINDEX_PAGES,
];
